import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login({ email, password });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || t('login.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageToggle = () => {
    const nextLang = i18n.language.startsWith('fr') ? 'en' : 'fr';
    i18n.changeLanguage(nextLang);
    localStorage.setItem('fleet_language', nextLang);
  };

  const currentLang = i18n.language.startsWith('fr') ? 'fr' : 'en';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Language toggle — top right corner */}
      <div className="absolute top-4 right-6">
        <button
          onClick={handleLanguageToggle}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold border border-gray-300 rounded-md bg-white hover:bg-gray-100 transition-colors"
          aria-label={currentLang === 'en' ? 'Switch language to French' : 'Changer de langue en anglais'}
        >
          <span className={currentLang === 'en' ? 'text-gray-900' : 'text-gray-400'}>
            {t('language.en')}
          </span>
          <span className="text-gray-300">|</span>
          <span className={currentLang === 'fr' ? 'text-gray-900' : 'text-gray-400'}>
            {t('language.fr')}
          </span>
        </button>
      </div>

      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="w-11 h-11 bg-[#0F172A] rounded-xl flex items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
                  <circle cx="7" cy="17" r="2"/>
                  <path d="M9 17h6"/>
                  <circle cx="17" cy="17" r="2"/>
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">{t('login.title')}</h1>
            </div>
            <p className="text-gray-600 mt-2">{t('login.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label={t('login.emailLabel')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
              autoComplete="email"
            />

            <Input
              label={t('login.passwordLabel')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.passwordPlaceholder')}
              required
              autoComplete="current-password"
            />

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            <Button type="submit" className="w-full" isLoading={isLoading}>
              {t('login.loginButton')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a href="#" className="text-sm text-primary-600 hover:text-primary-800">
              {t('login.forgotPassword')}
            </a>
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-gray-600">
          <p>{t('login.demoCredentials')}</p>
        </div>
      </div>
    </div>
  );
};
